/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import OAuthButtons from "../components/auth/OAuthButtons";
import type { AuthNotice } from "./auth/OAuthCallback";
import { toast } from 'sonner';
import { BarChart3, CalendarDays, Eye, EyeOff, FileText, Handshake, ListChecks, Target, Users, Wallet } from "lucide-react";
import { assets } from "../assets/assets";
import { post, completeSignIn, logout } from "../utils/service";

interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    user: {
      id: string;
      first_name: string;
      last_name: string;
      email: string;
      phone: string;
      role: string;
      preferred_currency: string;
      is_active: boolean;
      created_at: string;
      updated_at: string;
    };
    token: string;
    refresh_token: string;
  };
  timestamp: string;
}

const fieldClass = "w-full rounded-full border border-gray-200 bg-white px-5 py-3.5 text-sm text-gray-900 placeholder:text-gray-400 transition focus:border-[#ff6d27] focus:outline-none disabled:opacity-60";


const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Only same-app paths are allowed as a return address.
  const next = (() => { const n = searchParams.get("next") ?? ""; return n.startsWith("/") && !n.startsWith("//") ? n : "/"; })();
  const notice = (location.state as { notice?: AuthNotice } | null)?.notice;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);


  const validateEmail = (value: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let valid = true;

    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address.");
      valid = false;
    } else {
      setEmailError("");
    }

    if (valid) {
      setLoading(true);
      
      try {
        const loginResponse = await post<LoginResponse>('/auth/login', {
          email,
          password
        });

        if (loginResponse.success) {
          const { hasOrganization, platformAdmin } = await completeSignIn(loginResponse.data, rememberMe);
          if (platformAdmin) {
            toast.success(loginResponse.message);
            navigate("/platform");
            return;
          }
          if (!hasOrganization) {
            toast.error("Your account isn't part of an active organization yet. Ask your administrator to invite you.");
            logout();
            return;
          }
          toast.success(loginResponse.message);
          navigate(next);
          setEmail("");
          setPassword("");
        }
      } catch (error: any) {
        const status = error.response?.status;
        toast.error(status === 401
          ? "That email and password don't match. Check them and try again, or reset your password."
          : status === 429
            ? "Too many attempts. Please wait a minute and try again."
            : error.response?.data?.message || "Could not sign in. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[#fff4ee] font-geist">
      {/* Soft background: the brand panel's colour bleeds off the right edge, with floating dots. */}
      <div aria-hidden className="absolute inset-y-0 right-0 hidden w-1/2 bg-gradient-to-br from-[#ff7a3a] to-[#ff5a14] lg:block" />
      <div aria-hidden className="pointer-events-none absolute inset-0">
        {[["8%", "12%", 14], ["38%", "4%", 8], ["24%", "86%", 10], ["62%", "92%", 16], ["90%", "18%", 12], ["78%", "70%", 8]].map(([l, t, s], i) => (
          <span key={i} className="absolute rounded-full bg-white/50" style={{ left: l as string, top: t as string, width: s as number, height: s as number }} />
        ))}
      </div>

      <div className="relative flex min-h-screen items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-5xl rounded-[2rem] border border-[#ffd9c7] bg-white/40 p-2 backdrop-blur-sm sm:p-3">
          <div className="grid overflow-hidden rounded-[1.6rem] border border-gray-100 bg-white lg:grid-cols-2">
            {/* ---------------- form ---------------- */}
            <section className="flex items-center px-6 py-10 sm:px-12 sm:py-14">
              <div className="w-full">
                <img src={assets.MainLogo} alt="Pelarhive" className="mb-8 h-10 w-auto object-contain lg:hidden" />
                <h1 className="animate-element animate-delay-100 text-2xl font-semibold tracking-tight text-gray-900 sm:text-[1.75rem]">Sign in to your workspace</h1>
                <p className="animate-element animate-delay-200 mt-2 text-xs text-gray-500">Manage leads, deals, invoices and your team in one place.</p>

                {notice && (
                  <div className={`mt-6 rounded-2xl border px-4 py-3 text-xs ${notice.tone === "error" ? "border-red-200 bg-red-50 text-red-800" : "border-[#ff6d27]/30 bg-[#ff6d27]/5 text-gray-800"}`}>
                    <p className="font-semibold">{notice.title}</p>
                    <p className="mt-1 leading-relaxed">{notice.message}</p>
                  </div>
                )}

                <div className="animate-element animate-delay-300 mt-8">
                  <OAuthButtons layout="row" label="Sign in with" />
                </div>

                <form className="mt-6 space-y-5" onSubmit={handleSignIn}>
                  <div className="animate-element animate-delay-300">
                    <label htmlFor="email" className="mb-2 block text-xs font-medium text-gray-700">Email address</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      className={fieldClass}
                      disabled={loading}
                      required
                    />
                    {emailError && <p className="mt-1.5 text-xs text-red-500">{emailError}</p>}
                  </div>

                  <div className="animate-element animate-delay-400">
                    <label htmlFor="password" className="mb-2 block text-xs font-medium text-gray-700">Password</label>
                    <div className="relative">
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Your password"
                        className={`${fieldClass} pr-12`}
                        disabled={loading}
                        required
                      />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? "Hide password" : "Show password"}
                        className="absolute inset-y-0 right-4 flex items-center text-gray-400 hover:text-gray-700">
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <label className="animate-element animate-delay-500 flex w-fit cursor-pointer items-center gap-2.5 text-xs text-gray-600">
                    <input
                      type="checkbox"
                      name="rememberMe"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      disabled={loading}
                      className="h-3.5 w-3.5 rounded border-gray-300 accent-[#ff6d27]"
                    />
                    Keep me signed in
                  </label>

                  <div className="animate-element animate-delay-600 flex flex-col-reverse items-center gap-4 pt-2 sm:flex-row sm:justify-between">
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full rounded-full border border-[#ff6d27] bg-[#ff6d27] px-10 py-3.5 text-sm font-semibold text-white transition hover:border-[#f25d15] hover:bg-[#f25d15] disabled:opacity-60 sm:w-auto sm:min-w-[11rem]"
                    >
                      {loading ? "Signing in…" : "Sign in"}
                    </button>
                    <Link to="/forgot-password" className="text-xs font-semibold text-[#ff6d27] hover:underline">Forgot password?</Link>
                  </div>
                </form>

                <p className="mt-10 text-xs text-gray-500">
                  New here? Accounts are created by invitation. Ask your organization's administrator to invite you.
                </p>
              </div>
            </section>

            {/* ---------------- brand panel ---------------- */}
            <section className="relative hidden overflow-hidden bg-gradient-to-br from-[#ff7a3a] to-[#ff5a14] px-10 py-12 text-white lg:flex lg:flex-col">
              <div aria-hidden className="absolute -right-24 top-1/3 h-[34rem] w-[34rem] rounded-full border border-white/15" />
              <div aria-hidden className="absolute -left-32 -bottom-40 h-[30rem] w-[30rem] rounded-full border border-white/10" />
              <div className="relative flex justify-end">
                <img src={assets.MainLogo} alt="Pelarhive" className="h-9 w-auto object-contain brightness-0 invert" />
              </div>
              <div className="relative flex flex-1 items-center justify-center py-6">
                <BrandNetwork />
              </div>
              <div className="relative text-center">
                <h2 className="text-xl font-semibold">Your whole sales cycle, connected</h2>
                <p className="mx-auto mt-3 max-w-sm text-xs leading-relaxed text-white/80">
                  From the first lead to the last payment: pipelines, quotes, contracts, invoices and expenses, shared by your whole team.
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

/** The brand panel's picture: the app's modules around its hub, joined by dashed lines. */
function BrandNetwork() {
  const nodes = [
    { x: 18, y: 18, icon: Users, label: "Leads", tone: "bg-emerald-400" },
    { x: 82, y: 18, icon: Handshake, label: "Deals", tone: "bg-amber-300" },
    { x: 18, y: 82, icon: FileText, label: "Invoices", tone: "bg-sky-300" },
    { x: 82, y: 82, icon: ListChecks, label: "Tasks", tone: "bg-violet-300" },
  ];
  const chips = [
    { x: 50, y: 10, icon: Target }, { x: 90, y: 50, icon: BarChart3 }, { x: 10, y: 50, icon: Wallet }, { x: 50, y: 90, icon: CalendarDays },
  ];
  return (
    <div className="relative aspect-square w-full max-w-[19rem]">
      <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full" aria-hidden>
        {nodes.map((n) => <line key={n.label} x1="50" y1="50" x2={n.x} y2={n.y} stroke="white" strokeOpacity="0.55" strokeWidth="0.6" strokeDasharray="2 2" />)}
        <path d="M18 18 Q50 0 82 18 M82 18 Q100 50 82 82 M82 82 Q50 100 18 82 M18 82 Q0 50 18 18" fill="none" stroke="white" strokeOpacity="0.3" strokeWidth="0.5" strokeDasharray="2 2" />
      </svg>
      <div className="absolute left-1/2 top-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-8 border-white/10 bg-white/15 backdrop-blur">
        <img src={assets.SideBarClosed} alt="" className="h-10 w-10 object-contain brightness-0 invert" />
      </div>
      {nodes.map(({ x, y, icon: Icon, label, tone }) => (
        <div key={label} className="absolute flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-1.5" style={{ left: `${x}%`, top: `${y}%` }}>
          <span className={`flex h-[4.5rem] w-[4.5rem] items-center justify-center rounded-full ${tone} border-4 border-white/80 text-gray-900`}>
            <Icon className="h-7 w-7" />
          </span>
          <span className="text-[11px] font-medium text-white/90">{label}</span>
        </div>
      ))}
      {chips.map(({ x, y, icon: Icon }, i) => (
        <span key={i} className="absolute flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-[#ffd9c7] bg-white text-[#ff6d27]" style={{ left: `${x}%`, top: `${y}%` }}>
          <Icon className="h-4 w-4" />
        </span>
      ))}
    </div>
  );
}

export default Login;